/** Clinic local time for finance period boundaries. Clinics do not store a timezone today. */
export const CLINIC_TIMEZONE = 'Europe/Copenhagen';

export interface CivilDate {
  year: number;
  month: number;
  day: number;
}

export interface CivilDateTime extends CivilDate {
  hour: number;
  minute: number;
  second: number;
}

function partNumber(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): number {
  const value = parts.find((part) => part.type === type)?.value;
  return value ? Number(value) : 0;
}

export function zonedDateTime(date: Date, timeZone = CLINIC_TIMEZONE): CivilDateTime {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date);
  return {
    year: partNumber(parts, 'year'),
    month: partNumber(parts, 'month'),
    day: partNumber(parts, 'day'),
    hour: partNumber(parts, 'hour'),
    minute: partNumber(parts, 'minute'),
    second: partNumber(parts, 'second'),
  };
}

export function zonedYmd(date: Date, timeZone = CLINIC_TIMEZONE): string {
  const { year, month, day } = zonedDateTime(date, timeZone);
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function timezoneOffsetMs(date: Date, timeZone: string): number {
  const zoned = zonedDateTime(date, timeZone);
  const asUtc = Date.UTC(
    zoned.year,
    zoned.month - 1,
    zoned.day,
    zoned.hour,
    zoned.minute,
    zoned.second,
  );
  return asUtc - date.getTime() + date.getMilliseconds();
}

/** Instant that is the given civil clock time in the clinic timezone. */
export function zonedInstant(
  year: number,
  month: number,
  day: number,
  hour = 0,
  minute = 0,
  second = 0,
  millisecond = 0,
  timeZone = CLINIC_TIMEZONE,
): Date {
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute, second, millisecond);
  const first = new Date(utcGuess - timezoneOffsetMs(new Date(utcGuess), timeZone));
  return new Date(utcGuess - timezoneOffsetMs(first, timeZone));
}

export function startOfZonedDay(date: Date, timeZone = CLINIC_TIMEZONE): Date {
  const { year, month, day } = zonedDateTime(date, timeZone);
  return zonedInstant(year, month, day, 0, 0, 0, 0, timeZone);
}

export function endOfZonedDay(date: Date, timeZone = CLINIC_TIMEZONE): Date {
  const next = addZonedDays(startOfZonedDay(date, timeZone), 1, timeZone);
  return new Date(next.getTime() - 1);
}

export function addZonedDays(date: Date, days: number, timeZone = CLINIC_TIMEZONE): Date {
  const { year, month, day } = zonedDateTime(date, timeZone);
  const utc = Date.UTC(year, month - 1, day + days);
  const shifted = new Date(utc);
  return zonedInstant(
    shifted.getUTCFullYear(),
    shifted.getUTCMonth() + 1,
    shifted.getUTCDate(),
    0,
    0,
    0,
    0,
    timeZone,
  );
}

export function startOfZonedWeek(date: Date, timeZone = CLINIC_TIMEZONE): Date {
  const start = startOfZonedDay(date, timeZone);
  const weekday = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'short' }).format(start);
  const mondayBased: Record<string, number> = {
    Mon: 0,
    Tue: 1,
    Wed: 2,
    Thu: 3,
    Fri: 4,
    Sat: 5,
    Sun: 6,
  };
  return addZonedDays(start, -(mondayBased[weekday] ?? 0), timeZone);
}

export function endOfZonedWeek(date: Date, timeZone = CLINIC_TIMEZONE): Date {
  return endOfZonedDay(addZonedDays(startOfZonedWeek(date, timeZone), 6, timeZone), timeZone);
}

export function startOfZonedMonth(date: Date, timeZone = CLINIC_TIMEZONE): Date {
  const { year, month } = zonedDateTime(date, timeZone);
  return zonedInstant(year, month, 1, 0, 0, 0, 0, timeZone);
}

export function endOfZonedMonth(date: Date, timeZone = CLINIC_TIMEZONE): Date {
  const { year, month } = zonedDateTime(date, timeZone);
  return endOfZonedDay(zonedInstant(year, month + 1, 0, 0, 0, 0, 0, timeZone), timeZone);
}

export function startOfZonedYear(date: Date, timeZone = CLINIC_TIMEZONE): Date {
  const { year } = zonedDateTime(date, timeZone);
  return zonedInstant(year, 1, 1, 0, 0, 0, 0, timeZone);
}

export function endOfZonedYear(date: Date, timeZone = CLINIC_TIMEZONE): Date {
  const { year } = zonedDateTime(date, timeZone);
  return endOfZonedDay(zonedInstant(year, 12, 31, 0, 0, 0, 0, timeZone), timeZone);
}

export function addZonedMonths(date: Date, months: number, timeZone = CLINIC_TIMEZONE): Date {
  const { year, month, day } = zonedDateTime(date, timeZone);
  return zonedInstant(year, month + months, day, 0, 0, 0, 0, timeZone);
}

export function addZonedYears(date: Date, years: number, timeZone = CLINIC_TIMEZONE): Date {
  const { year, month, day } = zonedDateTime(date, timeZone);
  return zonedInstant(year + years, month, day, 0, 0, 0, 0, timeZone);
}

export function parseClinicDate(isoDate: string, timeZone = CLINIC_TIMEZONE): Date {
  const [year, month, day] = isoDate.split('-').map(Number);
  return zonedInstant(year, month, day, 0, 0, 0, 0, timeZone);
}

export function formatZonedDate(
  iso: string | null | undefined,
  locale = 'da-DK',
  timeZone = CLINIC_TIMEZONE,
): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

export function formatZonedDateTime(
  iso: string,
  locale = 'da-DK',
  timeZone = CLINIC_TIMEZONE,
): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, {
    timeZone,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

/** True when the stored clock is local noon. Manual payments used that as a date-only placeholder. */
export function isLocalNoon(iso: string | null | undefined, timeZone = CLINIC_TIMEZONE): boolean {
  if (!iso) return false;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return false;
  const zoned = zonedDateTime(date, timeZone);
  return zoned.hour === 12 && zoned.minute === 0 && zoned.second === 0;
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/** dd-mm-yyyy in the clinic timezone. */
export function formatClinicDate(
  iso: string | null | undefined,
  timeZone = CLINIC_TIMEZONE,
): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const zoned = zonedDateTime(date, timeZone);
  return `${pad2(zoned.day)}-${pad2(zoned.month)}-${zoned.year}`;
}

/** dd-mm-yyyy kl. HH:MM in the clinic timezone. */
export function formatClinicDateTime(
  iso: string | null | undefined,
  timeZone = CLINIC_TIMEZONE,
): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const zoned = zonedDateTime(date, timeZone);
  return `${pad2(zoned.day)}-${pad2(zoned.month)}-${zoned.year} kl. ${pad2(zoned.hour)}:${pad2(zoned.minute)}`;
}
